import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import React from 'react';
import uuid from 'uuid/v4';

import {LoadingSpinner} from 'app/components/AnalyzePolygonChart/AnalyzePolygonChart';
import {LayoutWrapper} from 'app/components/LayoutWrapper';
import Modal from 'app/components/Modal/Modal';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization/types';
import {LoggedInAuth, LoggedInUserActions} from 'app/providers/AuthProvider';
import colors from 'app/utils/colorUtils';

import {CreateNewWorkflow} from './CreateNewWorkflow';
import {ApiAgentWorkflow, useAgentWorkflows} from './hooks';
import * as cs from './styles.styl';

type WorkflowsPageProps = {
  history: any;
  auth: LoggedInAuth;
  profile: I.ImmutableOf<ApiOrganizationUser>;
  organization: I.ImmutableOf<ApiOrganization>;
  loggedInUserActions: LoggedInUserActions;
};

const WorkflowsPage: React.FC<WorkflowsPageProps> = ({
  history,
  auth,
  profile,
  organization,
  loggedInUserActions,
}) => {
  return (
    <LayoutWrapper
      history={history}
      auth={auth}
      profile={profile}
      organization={organization}
      loggedInUserActions={loggedInUserActions}
    >
      <Workflows organizationId={organization.get('id')} history={history} />
    </LayoutWrapper>
  );
};

type WorkflowsProps = {
  organizationId: string;
  history: any;
};

const Workflows: React.FC<WorkflowsProps> = ({organizationId, history}) => {
  const {data: workflows, isLoading} = useAgentWorkflows(organizationId);
  const [createNewModalOpen, setCreateNewModalOpen] = React.useState(false);

  //TODO(eva): scaffolding to "save" a workflow in state, rip this out when we can legit save workflows
  const [stateWorkflows, setStateWorkflows] = React.useState<ApiAgentWorkflow[]>(workflows || []);
  React.useEffect(() => {
    setStateWorkflows(workflows || []);
  }, [workflows]);

  if (isLoading) {
    return (
      <div style={{padding: '20px', textAlign: 'center'}}>
        <LoadingSpinner />
        <div style={{marginTop: '10px'}}>Loading workflows...</div>
      </div>
    );
  }

  if (!stateWorkflows || Object.keys(stateWorkflows).length === 0) {
    return (
      <div style={{padding: '20px', textAlign: 'center'}}>
        <B.Callout intent="warning" title="No workflows available">
          No agent workflows are configured for this organization.
        </B.Callout>
      </div>
    );
  }

  return (
    <div className={cs.page}>
      <div className={cs.pageContent}>
        <h2>Lens Agent</h2>
        <B.Callout
          icon={<B.Icon icon="clean" color={colors.purple} />}
          className={cs.activeLensAgent}
          style={{borderRadius: '0.5rem'}}
        >
          <p>
            Your AI-powered partner for monitoring efficiently and at scale. Tell the Lens Agent
            what to monitor and it'll handle the rest from reviewing imagery, looking for changes,
            and drafting notes. You stay in the loop, it does the heavy lifting.
          </p>
          <B.Button
            text="Create workflow"
            icon="plus"
            intent="primary"
            className={cs.agentAction}
            large
            onClick={() => setCreateNewModalOpen(true)}
          />
        </B.Callout>

        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <h3>Monitoring workflows</h3>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
          }}
        >
          {stateWorkflows.map((workflow) => {
            const latestRunId =
              workflow.runs.length > 0
                ? workflow.runs.sort((a, b) => (b.startedAt > a.startedAt ? 1 : -1))[0].runId
                : null;
            return (
              <B.Card
                key={workflow.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderRadius: '0.5rem',
                }}
                interactive={true}
                onClick={() => {
                  history.push(`/${organizationId}/agent/workflows/${workflow.id}`);
                }}
              >
                <div>
                  <h3 style={{margin: '0'}}>{workflow.name}</h3>
                  <div className={cs.runTime}>
                    <div>{workflow.featureIds.length} properties</div>
                    <div>•</div>
                    <div>{workflow.cadence || 'monthly'} cadence</div>
                  </div>
                </div>

                <div style={{display: 'flex', gap: '1rem'}}>
                  {latestRunId && (
                    <B.Button
                      text="Review latest results"
                      minimal
                      onClick={(e) => {
                        e.stopPropagation();
                        history.push(
                          `/${organizationId}/agent/workflows/${workflow.id}/runs/${latestRunId}`
                        );
                      }}
                    />
                  )}
                  <B.Button
                    text="Run Now"
                    icon="clean"
                    intent="primary"
                    className={cs.agentAction}
                    onClick={(e) => {
                      e.stopPropagation();
                      const runId = uuid();
                      history.push(
                        `/${organizationId}/agent/workflows/${workflow.id}/runs/${runId}?new=true`
                      );
                    }}
                  />
                </div>
              </B.Card>
            );
          })}
        </div>
      </div>
      <Modal
        className={cs.modelConfigurationContainer}
        headerText="Create new workflow"
        isOpen={createNewModalOpen}
        onClose={() => setCreateNewModalOpen(false)}
        hideActions
      >
        <CreateNewWorkflow
          //TODO(eva): also make this take a workflow for edit mode
          featureCollectionId={4243}
          onSave={(workflow: ApiAgentWorkflow) => {
            setStateWorkflows([workflow, ...stateWorkflows]);
            setCreateNewModalOpen(false);
          }}
        />
      </Modal>
    </div>
  );
};

export default WorkflowsPage;
